import { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from './supabase';
import * as shared from './services/shared';
import * as profileSvc from './services/profile';
import * as habitsSvc from './services/habits';
import * as goalsSvc from './services/goals';
import * as versionsSvc from './services/versions';
import * as cyclesSvc from './services/cycles';
import * as archivesSvc from './services/archives';
import * as pulseSvc from './services/pulse';
import * as notificationsSvc from './services/notifications';
import * as dashboardSvc from './services/dashboard';
import type { ServiceContext, ProfileCacheEntry } from './services/types';
import {
    Profile, Version, Cycle, Habit, HabitLog,
    Goal, ArchiveItem, Slide, DashboardData, Mantra, IdentityItem,
    EntityStatus, GoalType, ArchiveType, CycleDebrief,
    PulseCheckin, PulseInsights,
    UserSettings,
} from '@/types/types';

export { withTransform } from './imageUtils';

export class SupabaseService {
    private client: SupabaseClient;
    private cachedAuthUser: { id: string; created_at: string } | null = null;
    private _profileCache: ProfileCacheEntry | null = null;

    constructor(client?: SupabaseClient) {
        this.client = client || supabase;
    }

    private async ctx(): Promise<ServiceContext> {
        const userId = await this.getUserId();
        return {
            client: this.client,
            userId,
            invalidateProfile: () => { this._profileCache = null; },
            getProfileCache: () => this._profileCache,
            setProfileCache: (v) => { this._profileCache = v; },
        };
    }

    // -- AUTH & ALIASING --
    clearAuthCache() {
        this.cachedAuthUser = null;
        this._profileCache = null;
    }

    private async getAuthUser() {
        if (this.cachedAuthUser) return this.cachedAuthUser;
        const { data: { user }, error } = await this.client.auth.getUser();
        if (error || !user) throw new Error("Not authenticated");
        this.cachedAuthUser = { id: user.id, created_at: user.created_at };
        return this.cachedAuthUser;
    }

    async getUserId(): Promise<string> {
        return (await this.getAuthUser()).id;
    }

    async getAccountCreatedAt(): Promise<string> {
        return (await this.getAuthUser()).created_at;
    }

    async getAuthSessionUser(): Promise<{ email: string; name: string } | null> {
        return profileSvc.getAuthSessionUser(this.client);
    }

    async verifyEmailToken(token: string, email: string): Promise<{ success: boolean; message: string }> {
        return profileSvc.verifyEmailToken(this.client, token, email);
    }

    getLocalDateString(date: Date = new Date()): string { return shared.getLocalDateString(date); }

    // Helper to get the full profile
    async getProfile(): Promise<Profile | null> {
        const { client, userId, getProfileCache, setProfileCache } = await this.ctx();
        return profileSvc.getProfile(client, userId, getProfileCache, setProfileCache);
    }

    // -- SYSTEM SETTINGS --
    async getSystemSettings(): Promise<{ days: number; cycles: number }> {
        const { client, userId, getProfileCache, setProfileCache } = await this.ctx();
        return profileSvc.getSystemSettings(client, userId, getProfileCache, setProfileCache);
    }

    async updateProfile(updates: Partial<Profile>): Promise<void> {
        const { client, userId, invalidateProfile } = await this.ctx();
        return profileSvc.updateProfile(client, userId, updates, invalidateProfile);
    }

    /**
     * Marking the profile onboarded comes FIRST and is the only step allowed to
     * throw. The default-archive seed is once-per-account work, so this is its
     * home, but OnboardingOverlay awaits this call: a seed failure that
     * propagated would trap the user inside onboarding forever. It is therefore
     * logged and swallowed on purpose. Do not "improve" this by re-throwing.
     * Vision and Life Checklist are singletons the Archives section auto-creates
     * when absent, so an unseeded account self-heals for those two.
     */
    async completeOnboarding(): Promise<void> {
        const { client, userId, invalidateProfile } = await this.ctx();
        await profileSvc.completeOnboarding(client, userId, invalidateProfile);
        try {
            await archivesSvc.seedDefaultArchives(client, userId, ArchiveType);
        } catch (err) {
            console.error('[onboarding] Failed to seed default archives:', err);
        }
    }

    async completeTour(): Promise<void> {
        const { client, userId, invalidateProfile } = await this.ctx();
        return profileSvc.completeTour(client, userId, invalidateProfile);
    }

    async uploadAvatar(file: File): Promise<string> {
        const { client, userId } = await this.ctx();
        return profileSvc.uploadAvatar(client, userId, file);
    }

    async removeAvatar(): Promise<void> {
        const { client, userId, invalidateProfile } = await this.ctx();
        await profileSvc.removeAvatar(client, userId);
        invalidateProfile();
    }

    async updateSystemSettings(days: number, cycles: number): Promise<void> {
        const { client, userId } = await this.ctx();
        return profileSvc.updateSystemSettings(client, userId, days, cycles);
    }

    // -- USER SETTINGS (JSONB on profiles) --
    async getUserSettings(): Promise<UserSettings> {
        const { client, userId, getProfileCache, setProfileCache } = await this.ctx();
        return profileSvc.getUserSettings(client, userId, getProfileCache, setProfileCache);
    }

    async updateUserSettings(settings: Partial<UserSettings>): Promise<void> {
        const { client, userId, getProfileCache, setProfileCache, invalidateProfile } = await this.ctx();
        return profileSvc.updateUserSettings(client, userId, settings, getProfileCache, setProfileCache, invalidateProfile);
    }

    async getStorageUsage(): Promise<{ files: { name: string; size: number }[]; totalBytes: number }> {
        const { client, userId } = await this.ctx();
        return profileSvc.getStorageUsage(client, userId);
    }

    // Estimated average bytes per row for each table (source of truth is services/profile.ts)
    static readonly ROW_SIZE_ESTIMATES: Record<string, number> = profileSvc.ROW_SIZE_ESTIMATES;

    async getDataRecordCounts(): Promise<{ counts: Record<string, number>; estimatedBytes: number }> {
        const { client, userId } = await this.ctx();
        return profileSvc.getDataRecordCounts(client, userId);
    }

    async changePassword(newPassword: string): Promise<void> {
        return profileSvc.changePassword(this.client, newPassword);
    }

    // -- ANALYTICS ALGORITHMS (Migrated from MockDB) --

    calculateDailyScore(habits: Habit[], logs: HabitLog[]): number { return shared.calculateDailyScore(habits, logs); }

    // -- DATA FETCHING --

    async getDashboardData(): Promise<DashboardData> {
        const { client, userId, getProfileCache, setProfileCache } = await this.ctx();
        return dashboardSvc.getDashboardData(client, userId, getProfileCache, setProfileCache);
    }

    // -- ACTIONS --

    async toggleHabit(habitId: string, isChecked: boolean): Promise<void> {
        const { client, userId } = await this.ctx();
        return habitsSvc.toggleHabit(client, userId, habitId, isChecked);
    }

    // Toggle a habit log for a specific date (used by History page for retroactive edits)
    async toggleHabitForDate(habitId: string, date: string, isChecked: boolean): Promise<void> {
        const { client, userId } = await this.ctx();
        return habitsSvc.toggleHabitForDate(client, userId, habitId, date, isChecked);
    }

    // -- SLIDES (Storage + DB) --
    async getSlides(): Promise<Slide[]> {
        const { client, userId } = await this.ctx();
        return archivesSvc.getSlides(client, userId);
    }

    async updateSlide(slide: Slide, imageFile?: File): Promise<void> {
        const { client, userId } = await this.ctx();
        return archivesSvc.updateSlide(client, userId, slide, imageFile);
    }

    async createSlide(): Promise<string> {
        const { client, userId } = await this.ctx();
        return archivesSvc.createSlide(client, userId);
    }

    async deleteSlide(id: string): Promise<void> {
        const { client, userId } = await this.ctx();
        return archivesSvc.deleteSlide(client, userId, id);
    }

    // -- ARCHIVES --
    async getArchives(): Promise<ArchiveItem[]> {
        const { client, userId } = await this.ctx();
        return archivesSvc.getArchives(client, userId);
    }

    async updateArchive(item: ArchiveItem): Promise<{ id: string }> {
        const { client, userId } = await this.ctx();
        return archivesSvc.updateArchive(client, userId, item);
    }

    async deleteArchive(id: string): Promise<void> {
        const { client, userId } = await this.ctx();
        return archivesSvc.deleteArchive(client, userId, id);
    }

    // -- HISTORY & ANALYTICS --

    async getHistoryData(days: number = 90): Promise<{
        dailyScores: { date: string; score: number; details: { habit: Habit; done: boolean }[] }[],
        cycles: Cycle[],
        habitStats: Record<string, { currentStreak: number; maxStreak: number; consistency: number }>
    }> {
        const { client, userId } = await this.ctx();
        return dashboardSvc.getHistoryData(client, userId, days);
    }

    // -- VERSION MANAGEMENT --
    async createVersion(data: { title: string; description: string; number: number; startDate: string }, options?: { setActive?: boolean }): Promise<Version> {
        const { client, userId, invalidateProfile, getProfileCache, setProfileCache } = await this.ctx();
        const profile = await profileSvc.getProfile(client, userId, getProfileCache, setProfileCache);
        return versionsSvc.createVersion(client, userId, data, profile, invalidateProfile, EntityStatus, options);
    }

    // -- GOAL MANAGEMENT --
    async updateGoal(updatedGoal: Goal): Promise<void> {
        const { client, userId } = await this.ctx();
        return goalsSvc.updateGoal(client, userId, updatedGoal);
    }

    // -- MANTRA MANAGEMENT --
    async getMantraItems(): Promise<Mantra[]> {
        const { client, userId } = await this.ctx();
        return archivesSvc.getMantraItems(client, userId);
    }

    async getMantras(): Promise<string[]> {
        const { client, userId } = await this.ctx();
        return archivesSvc.getMantras(client, userId);
    }

    async addMantra(text: string): Promise<Mantra> {
        const { client, userId } = await this.ctx();
        return archivesSvc.addMantra(client, userId, text);
    }

    async deleteMantra(id: string): Promise<void> {
        const { client, userId } = await this.ctx();
        return archivesSvc.deleteMantra(client, userId, id);
    }

    // -- IDENTITY ITEMS (Skills & Characteristics Bank) --

    async getIdentityItems(): Promise<IdentityItem[]> {
        const { client, userId } = await this.ctx();
        return archivesSvc.getIdentityItems(client, userId);
    }

    async getAllSkillNames(): Promise<string[]> {
        const { client, userId } = await this.ctx();
        return archivesSvc.getAllSkillNames(client, userId);
    }

    async getAllCharacteristicNames(): Promise<string[]> {
        const { client, userId } = await this.ctx();
        return archivesSvc.getAllCharacteristicNames(client, userId);
    }

    async addIdentityItem(name: string, type: 'skill' | 'characteristic'): Promise<IdentityItem> {
        const { client, userId } = await this.ctx();
        return archivesSvc.addIdentityItem(client, userId, name, type);
    }

    async updateIdentityItem(id: string, name: string): Promise<void> {
        const { client, userId } = await this.ctx();
        return archivesSvc.updateIdentityItem(client, userId, id, name);
    }

    async deleteIdentityItem(id: string): Promise<void> {
        const { client, userId } = await this.ctx();
        return archivesSvc.deleteIdentityItem(client, userId, id);
    }

    // -- CYCLE CREATION (Complex Transaction) --
    async createCycle(
        cycleData: Omit<Cycle, 'id' | 'version_id'> & { version_id?: string },
        goalsData: Array<{ title: string; description: string; type: GoalType; subtasks?: string[]; habit?: { name: string; weight: number; category: string } }>,
        standaloneHabits: Array<{ name: string; weight: number; category: string }>,
        options?: { setActive?: boolean }
    ): Promise<Cycle> {
        const { client, userId, invalidateProfile, getProfileCache, setProfileCache } = await this.ctx();
        const profile = await profileSvc.getProfile(client, userId, getProfileCache, setProfileCache);
        const targetVersionId = cycleData.version_id || profile?.current_version_id;
        if (!targetVersionId) throw new Error("No active version");
        return cyclesSvc.createCycle(client, userId, cycleData, goalsData, standaloneHabits, targetVersionId, invalidateProfile, EntityStatus, options);
    }

    // -- LAB METHODS --

    async getLabData(versionId?: string): Promise<{ goals: Goal[]; version: Version | null; cycles: Cycle[]; habits: Habit[]; activeCycleId: string | null, habitLogs: HabitLog[] }> {
        const { client, userId, getProfileCache, setProfileCache } = await this.ctx();
        return dashboardSvc.getLabData(client, userId, getProfileCache, setProfileCache, versionId);
    }

    async updateCycle(cycle: Cycle): Promise<void> {
        const { client, userId } = await this.ctx();
        return cyclesSvc.updateCycle(client, userId, cycle);
    }

    async deleteCycle(cycleId: string): Promise<void> {
        const { client, userId, invalidateProfile, getProfileCache, setProfileCache } = await this.ctx();
        const profile = await profileSvc.getProfile(client, userId, getProfileCache, setProfileCache);
        return cyclesSvc.deleteCycle(client, userId, cycleId, profile?.current_cycle_id, invalidateProfile);
    }

    async setCycleActive(cycleId: string): Promise<void> {
        const { client, userId, invalidateProfile } = await this.ctx();
        return cyclesSvc.setCycleActive(client, userId, cycleId, invalidateProfile);
    }

    async setVersionActive(versionId: string): Promise<void> {
        const { client, userId, invalidateProfile } = await this.ctx();
        return versionsSvc.setVersionActive(client, userId, versionId, invalidateProfile);
    }

    // -- GOAL CRUD --
    async addGoal(goal: Goal): Promise<void> {
        const { client, userId } = await this.ctx();
        return goalsSvc.addGoal(client, userId, goal);
    }

    async deleteGoal(goalId: string): Promise<void> {
        const { client, userId } = await this.ctx();
        return goalsSvc.deleteGoal(client, userId, goalId);
    }

    // -- GOALS PAGE DATA --
    async getGoalsPageData(): Promise<{
        version: Version | null;
        currentCycle: Cycle | null;
        currentCycleGoals: Goal[];
        pastCycleGoals: Goal[];
        pastCycles: Cycle[];
        currentCycleHabits: Habit[];
        allHabits: Habit[];
        habitStats: Record<string, { currentStreak: number; maxStreak: number; consistency: number }>;
        habitLogs: HabitLog[];
    }> {
        const { client, userId, getProfileCache, setProfileCache } = await this.ctx();
        return dashboardSvc.getGoalsPageData(client, userId, getProfileCache, setProfileCache);
    }

    // -- CYCLE INSERT --
    async addCycle(cycle: Cycle): Promise<void> {
        const { client, userId } = await this.ctx();
        return cyclesSvc.addCycle(client, userId, cycle);
    }

    // -- GOAL LINEAGE --
    async carryForwardGoal(goal: Goal, newCycleId: string): Promise<Goal & { needsHabitLink?: boolean; originalHabit?: Habit }> {
        const { client, userId } = await this.ctx();
        return goalsSvc.carryForwardGoal(client, userId, goal, newCycleId, GoalType);
    }

    // -- HABIT CRUD --
    async getAllHabitLogs(): Promise<HabitLog[]> {
        const { client, userId } = await this.ctx();
        return habitsSvc.getAllHabitLogs(client, userId);
    }

    async getAllHabits(): Promise<Habit[]> {
        const { client, userId } = await this.ctx();
        return habitsSvc.getAllHabits(client, userId);
    }

    async addHabit(habit: Habit): Promise<void> {
        const { client, userId } = await this.ctx();
        return habitsSvc.addHabit(client, userId, habit);
    }

    async updateHabit(habit: Habit): Promise<void> {
        const { client, userId } = await this.ctx();
        return habitsSvc.updateHabit(client, userId, habit);
    }

    async deleteHabit(habitId: string): Promise<void> {
        const { client, userId } = await this.ctx();
        return habitsSvc.deleteHabit(client, userId, habitId);
    }

    // -- DATABASE PAGE HELPERS --

    async getAllVersions(): Promise<Version[]> {
        const { client, userId } = await this.ctx();
        return versionsSvc.getAllVersions(client, userId);
    }

    async updateVersion(version: Version): Promise<void> {
        const { client, userId } = await this.ctx();
        return versionsSvc.updateVersion(client, userId, version);
    }

    async deleteVersion(versionId: string): Promise<void> {
        const { client, userId } = await this.ctx();
        return versionsSvc.deleteVersion(client, userId, versionId);
    }

    async getAllCycles(): Promise<Cycle[]> {
        const { client, userId } = await this.ctx();
        return cyclesSvc.getAllCycles(client, userId);
    }

    async getAllGoals(): Promise<Goal[]> {
        const { client, userId } = await this.ctx();
        return goalsSvc.getAllGoals(client, userId);
    }

    // -- REORDERING --
    async reorderHabits(orderedIds: string[]): Promise<void> {
        const { client, userId } = await this.ctx();
        return habitsSvc.reorderHabits(client, userId, orderedIds);
    }

    async reorderGoals(orderedIds: string[]): Promise<void> {
        const { client, userId } = await this.ctx();
        return goalsSvc.reorderGoals(client, userId, orderedIds);
    }

    async getNextHabitSortOrder(cycleId: string): Promise<number> {
        const { client, userId } = await this.ctx();
        return habitsSvc.getNextHabitSortOrder(client, userId, cycleId);
    }

    async getNextGoalSortOrder(cycleId: string): Promise<number> {
        const { client, userId } = await this.ctx();
        return goalsSvc.getNextGoalSortOrder(client, userId, cycleId);
    }

    // -- NOTIFICATIONS (DB-backed) --

    async getActiveNotifications(): Promise<import('@/types/types').DBNotification[]> {
        const { client, userId } = await this.ctx();
        return notificationsSvc.getActiveNotifications(client, userId);
    }

    async dismissNotification(notificationKey: string): Promise<void> {
        const { client, userId } = await this.ctx();
        return notificationsSvc.dismissNotification(client, userId, notificationKey);
    }

    async dismissAllNotifications(): Promise<void> {
        const { client, userId } = await this.ctx();
        return notificationsSvc.dismissAllNotifications(client, userId);
    }

    async deleteNotificationByKey(notificationKey: string): Promise<void> {
        const { client, userId } = await this.ctx();
        return notificationsSvc.deleteNotificationByKey(client, userId, notificationKey);
    }

    async getCycleDebriefStats(cycleId: string): Promise<Omit<CycleDebrief, 'reflection' | 'completed_at'>> {
        const { client, userId } = await this.ctx();
        return cyclesSvc.getCycleDebriefStats(client, userId, cycleId, GoalType);
    }

    /** Read-only aggregation for the cycle report page: cycle, habits, logs, goals, pulse, and insights in one call. */
    async getCycleReportData(cycleId: string): Promise<cyclesSvc.CycleReportData> {
        const { client, userId } = await this.ctx();
        return cyclesSvc.getCycleReportData(client, userId, cycleId);
    }

    /** Version+sprint-facing entry point for the /cycle/v[versionNumber]/[sprintNumber]/report route — see cyclesSvc.getCycleReportDataByVersionSprint. */
    async getCycleReportDataByVersionSprint(versionNumber: number, sprintNumber: number): Promise<cyclesSvc.CycleReportData> {
        const { client, userId } = await this.ctx();
        return cyclesSvc.getCycleReportDataByVersionSprint(client, userId, versionNumber, sprintNumber);
    }

    /** Reverse lookup (cycle id -> {versionNumber, sprintNumber}), ownership-scoped — see cyclesSvc.getCycleVersionSprint. */
    async getCycleVersionSprint(cycleId: string): Promise<{ versionNumber: number; sprintNumber: number } | null> {
        const { client, userId } = await this.ctx();
        return cyclesSvc.getCycleVersionSprint(client, userId, cycleId);
    }

    async saveCycleDebrief(cycleId: string, debrief: CycleDebrief): Promise<void> {
        const { client, userId } = await this.ctx();
        return cyclesSvc.saveCycleDebrief(client, userId, cycleId, debrief);
    }

    /** D-03: command-palette "Open cycle report" target resolution — see cyclesSvc.getReportCommandTarget. */
    async getReportCommandTarget(): Promise<{ versionNumber: number; sprintNumber: number } | null> {
        const { client, userId } = await this.ctx();
        const profile = await this.getProfile();
        return cyclesSvc.getReportCommandTarget(client, userId, profile);
    }

    async getCyclesWithDebriefs(): Promise<Array<{
        id: string;
        sprint_number: number;
        start_date: string;
        end_date: string;
        debrief: CycleDebrief;
        version_number: number;
    }>> {
        const { client, userId } = await this.ctx();
        return cyclesSvc.getCyclesWithDebriefs(client, userId);
    }

    async signOut(): Promise<void> {
        await this.client.auth.signOut();
    }

    // -- DAILY PULSE (Pro only -- RLS enforces subscription_plan = 'pro') --

    async getTodayPulse(): Promise<PulseCheckin | null> {
        const { client, userId } = await this.ctx();
        return pulseSvc.getTodayPulse(client, userId);
    }

    async saveMorningCheckin(data: {
        sleep_bed_time?: string | null;
        sleep_wake_time?: string | null;
        sleep_quality: number | null;
        morning_mood: number | null;
        morning_energy?: number | null;
        morning_feelings: string[];
        one_thing?: string | null;
        focus_tags?: string[];
    }): Promise<PulseCheckin> {
        const { client, userId } = await this.ctx();
        return pulseSvc.saveMorningCheckin(client, userId, data);
    }

    async saveEveningCheckin(data: {
        evening_mood?: number | null;
        evening_performance: number | null;
        evening_stress: number | null;
        evening_motivation: number | null;
        evening_energy: number | null;
        evening_connectedness: number | null;
        evening_feelings: string[];
        one_thing_completed?: boolean | null;
        activity_tags: string[];
        reflection?: string | null;
    }): Promise<PulseCheckin> {
        const { client, userId } = await this.ctx();
        return pulseSvc.saveEveningCheckin(client, userId, data);
    }

    async resetPulseCheckin(mode: 'morning' | 'evening'): Promise<void> {
        const { client, userId } = await this.ctx();
        return pulseSvc.resetPulseCheckin(client, userId, mode);
    }

    async getPulseHistory(days: number = 30): Promise<PulseCheckin[]> {
        const { client, userId } = await this.ctx();
        return pulseSvc.getPulseHistory(client, userId, days);
    }

    /** Get the latest report for the current user */
    async getPulseInsights(): Promise<PulseInsights | null> {
        const { client, userId } = await this.ctx();
        return pulseSvc.getPulseInsights(client, userId);
    }

    /** Get report history (last N reports, newest first) */
    async getPulseInsightHistory(limit = 5): Promise<PulseInsights[]> {
        const { client, userId } = await this.ctx();
        return pulseSvc.getPulseInsightHistory(client, userId, limit);
    }

    /** Upsert a report for a specific period (composite key: user_id + period_start) */
    async upsertPulseInsights(
        insights: import('@/types/types').StoredInsight[],
        dataDays: number,
        periodStart: string,
        periodEnd: string,
        moodLabel: string | null,
        summary?: string | null,
    ): Promise<void> {
        const { client, userId } = await this.ctx();
        return pulseSvc.upsertPulseInsights(client, userId, insights, dataDays, periodStart, periodEnd, moodLabel, summary);
    }

    /** Delete all reports for the current user */
    async deletePulseInsights(): Promise<void> {
        const { client, userId } = await this.ctx();
        return pulseSvc.deletePulseInsights(client, userId);
    }

    async getUserActivityTags(): Promise<string[]> {
        const { client, userId } = await this.ctx();
        return pulseSvc.getUserActivityTags(client, userId);
    }

    async getPulseByDateRange(startDate: string, endDate: string): Promise<PulseCheckin[]> {
        const { client, userId } = await this.ctx();
        return pulseSvc.getPulseByDateRange(client, userId, startDate, endDate);
    }

    async getVersionById(id: string): Promise<Version | null> {
        const { client, userId } = await this.ctx();
        return versionsSvc.getVersionById(client, userId, id);
    }

    async getCycleById(id: string): Promise<Cycle | null> {
        const { client, userId } = await this.ctx();
        return cyclesSvc.getCycleById(client, userId, id);
    }

    async getActiveVersion(): Promise<{ number: number; title: string } | null> {
        const profile = await this.getProfile();
        if (!profile?.current_version_id) return null;
        const version = await this.getVersionById(profile.current_version_id);
        if (!version) return null;
        return { number: version.number, title: version.title };
    }
}

export const supabaseService = new SupabaseService();
